import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import { boolean, object, setLocale, string, array } from "yup";
import { en } from "yup-locales";
import { GlobalContext } from "../../../contexts/GlobalContext";
import {
  _ACTIONS,
  _COLORS,
  _HTTP,
  _MESSAGES,
  _SWAL_ICONS,
  _MEMBER,
} from "../../../utils/constants";
import { _ROUTES } from "../../../utils/_routes_constants";
import { RequestHelper } from "../../../utils/requestHelper";
import { createToast } from "../../../utils/toast";
import { BasicListContext } from "../../../contexts/BasicListContext";
import { getMuiTableConfig } from "../../../utils/MuiTableUtils/muiTableBuilder";
import { useMaterialReactTable } from "material-react-table";
import SourcesListColumns from "../../../utils/MuiTableUtils/ColumnBuilders/SourceListColums";
import { Box } from "@mui/material";
import { MuiTableComponent } from "../../helpers/MuiTableComponent";
import BasicModal from "../../helpers/BasicModal";
import { BasicFormikForm } from "../../form/BasicFormikForm";
import FactoryIcon from "@mui/icons-material/Factory";
import { buildOptions, getEntityProperties } from "../../../utils/formHelper";
import _ from "lodash";
import { createConfirm } from "../../../utils/confirm";

export const SourceList = ({
  editData,
  setEditData,
  showModal,
  setShowModal,
}) => {
  const { open } = useContext(GlobalContext);
  const [options, setOptions] = useState({});
  const v = object({
    name: string().required().label("Name"),
    active: boolean().required().label("Active"),
    sites: array().label("Sites"),
  });

  setLocale(en);

  const formik = useFormik({
    initialValues: {
      name: "",
      active: true,
      sites: [],
    },
    validationSchema: v,
    onSubmit: (values) => {
      const methode = editData ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url = _ROUTES.SOURCES + (editData ? `/${editData.id}` : "");

      const source = {
        name: values.name,
        active: values.active,
        sites: values.sites,
      };

      const rq = new RequestHelper();

      rq.doRequest(methode, url, source)
        .then((res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            setShowModal(false);
            setEditData(false);
            setRefresh((prev) => {
              return prev + 1;
            });
            createToast(_SWAL_ICONS.success, "Source saved successfully");
          } else {
            createToast(_SWAL_ICONS.error, _MESSAGES.error);
          }
        })
        .catch(() => {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        });
    },
  });

  const {
    rowCount,
    data,
    isLoaded,
    isError,
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
    isLoading,
    setRefresh,
  } = useContext(BasicListContext);

  // Get the table configuration
  const c = getMuiTableConfig(
    // Params
    { enableColumnFilters: true, enableExpanding: true },
    // States & setStates (from context) Have to be the same as this
    {
      rowCount,
      isLoaded,
      isError,
      pagination,
      setPagination,
      columnFilters,
      setColumnFilters,
      globalFilter,
      setGlobalFilter,
      sorting,
      setSorting,
      isLoading,
    },
  );

  const deleteSource = (source) => {
    const rq = new RequestHelper();
    createConfirm("Are you sure you want to delete this source?")
      .fire({})
      .then((result) => {
        if (result.isConfirmed) {
          // Processing
          const rq = new RequestHelper();

          rq.doRequest(_ACTIONS.DELETE, _ROUTES.SOURCES + `/${source.id}`)
            .then((res) => {
              if (res.status === 204) {
                createToast(
                  _SWAL_ICONS.success,
                  "The source has been deleted",
                ).then(() => {
                  setRefresh((prev) => prev++);
                });
              } else {
                createToast(_SWAL_ICONS.error, res.message);
              }
            })
            .catch(() => {
              createToast(_SWAL_ICONS.error, _MESSAGES.error);
            });
        }
      });
  };

  const columns = SourcesListColumns({
    setEditData,
    setShowModal,
    setRefresh,
    deleteSource,
  });

  const table = useMaterialReactTable({
    columns: columns,
    data: data,
    ...c,
    enableColumnResizing: false,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    initialState: {
      density: "compact",
    },
  });

  useEffect(() => {
    if (editData) {
      formik.setValues({
        name: editData.name,
        active: editData.active,
        sites: getEntityProperties(editData?.sites, "@id"),
      });
    } else {
      formik.resetForm();
    }
  }, [editData]);

  useEffect(() => {
    if (_.isEmpty(options)) {
      const rq = new RequestHelper();
      rq.doRequest(_ACTIONS.GET, _ROUTES.SITES, {
        pagination: false,
      }).then((res) => {
        if (res.status === _HTTP.ok) {
          setOptions({ sites: buildOptions(res.data[_MEMBER], "name", "@id") });
        }
      });
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: open ? window.innerWidth - 340 : window.innerWidth - 64,
          borderRadius: "1px",
          boxShadow: `0px 0px 2px ${_COLORS.grey} `,
          overflowX: "auto",
        }}
      >
        {/* Table Component is here */}
        <MuiTableComponent route={_ROUTES.SOURCES} table={table} />
      </Box>
      {/* Modal  */}
      <BasicModal
        showModal={showModal}
        setShowModal={setShowModal}
        title={"Source Form"}
        content={
          <BasicFormikForm
            formik={formik}
            setEditData={setEditData}
            setShowModal={setShowModal}
            validations={v}
            options={options}
          />
        }
        icon={<FactoryIcon />}
        width={"500"}
      />
    </>
  );
};

export default SourceList;
